/**
 * Bandeja de sons da pessoa.
 *
 * Fica fora da store do servidor porque não pertence a servidor nenhum: os
 * mesmos sons acompanham quem enviou para qualquer chamada de que participe.
 */

import { create } from 'zustand'
import type { UserSound } from '@nexus/shared'
import { LIMITS } from '@nexus/shared'
import { api } from '@/lib/api'
import { esquecerSom, prepararSom } from '@/lib/bandeja-de-sons'

interface SoundsState {
  sounds: UserSound[]
  /** Falso até a primeira busca terminar, para não piscar "bandeja vazia". */
  carregado: boolean
  carregando: boolean
  enviando: boolean
  erro: string | null
  /** Id do som que acabou de ser disparado, para a animação do botão. */
  tocandoAgora: string | null

  carregar: (forcar?: boolean) => Promise<void>
  enviar: (file: File, nome: string) => Promise<boolean>
  renomear: (soundId: string, nome: string) => Promise<void>
  remover: (soundId: string) => Promise<void>
  tocar: (soundId: string) => Promise<void>
  limparErro: () => void
}

function mensagemDe(err: unknown, padrao: string): string {
  return err instanceof Error && err.message ? err.message : padrao
}

export const useSoundsStore = create<SoundsState>()((set, get) => ({
  sounds: [],
  carregado: false,
  carregando: false,
  enviando: false,
  erro: null,
  tocandoAgora: null,

  carregar: async (forcar) => {
    const { carregado, carregando } = get()
    if (carregando || (carregado && !forcar)) return

    set({ carregando: true })
    try {
      const { sounds } = await api.sounds.list()
      set({ sounds, carregado: true, carregando: false })
    } catch (err) {
      set({ carregando: false, erro: mensagemDe(err, 'Não foi possível carregar seus áudios') })
    }
  },

  enviar: async (file, nome) => {
    if (get().sounds.length >= LIMITS.soundsPerUser) {
      set({ erro: `Sua bandeja já tem ${LIMITS.soundsPerUser} áudios. Apague algum para abrir espaço.` })
      return false
    }

    set({ enviando: true, erro: null })
    try {
      const som = await api.sounds.upload(file, nome)
      prepararSom(som.url)
      set((state) => ({ sounds: [som, ...state.sounds], enviando: false }))
      return true
    } catch (err) {
      set({ enviando: false, erro: mensagemDe(err, 'Não foi possível enviar o áudio') })
      return false
    }
  },

  renomear: async (soundId, nome) => {
    // Troca na tela antes da resposta: renomear não falha por conflito e o
    // campo voltando ao nome antigo por meio segundo parece defeito.
    const anterior = get().sounds
    set({
      sounds: anterior.map((s) => (s.id === soundId ? { ...s, name: nome } : s)),
      erro: null,
    })
    try {
      await api.sounds.rename(soundId, nome)
    } catch (err) {
      set({ sounds: anterior, erro: mensagemDe(err, 'Não foi possível renomear') })
    }
  },

  remover: async (soundId) => {
    const anterior = get().sounds
    const alvo = anterior.find((s) => s.id === soundId)
    set({ sounds: anterior.filter((s) => s.id !== soundId), erro: null })
    try {
      await api.sounds.remove(soundId)
      if (alvo) esquecerSom(alvo.url)
    } catch (err) {
      set({ sounds: anterior, erro: mensagemDe(err, 'Não foi possível apagar') })
    }
  },

  tocar: async (soundId) => {
    set({ tocandoAgora: soundId, erro: null })
    try {
      // Quem apertou também ouve pelo evento que volta do servidor. Tocar aqui
      // direto adiantaria o som em alguns milissegundos e desalinharia de todo
      // mundo na sala, que é pior do que a espera.
      await api.sounds.play(soundId)
    } catch (err) {
      set({ erro: mensagemDe(err, 'Não foi possível tocar o áudio') })
    } finally {
      window.setTimeout(() => {
        if (get().tocandoAgora === soundId) set({ tocandoAgora: null })
      }, 400)
    }
  },

  limparErro: () => set({ erro: null }),
}))
